import { formatTradeDateTimeDetailForDisplay } from "./displayDateTime";

export type SampleOrderLeg = {
  positionEffect?: string;
  quantity?: number;
  averagePrice?: number;
  positionCode?: string;
};

export type GroupedSampleRoundTrip = {
  positionCode: string;
  reference: string;
  instrument: string;
  open?: Record<string, unknown>;
  close?: Record<string, unknown>;
};

export function getSampleOrderLeg(order: Record<string, unknown>): SampleOrderLeg | undefined {
  const legs = order.legs;
  if (!Array.isArray(legs) || legs.length === 0) return undefined;
  return legs[0] as SampleOrderLeg;
}

export function getSampleOrderPositionEffect(order: Record<string, unknown>): string {
  return String(getSampleOrderLeg(order)?.positionEffect ?? "").toUpperCase();
}

export function extractReferenceFromPositionCode(positionCode: string): string {
  const trimmed = positionCode.trim();
  if (!trimmed) return "—";
  const parts = trimmed.split(":");
  return parts[parts.length - 1] || trimmed;
}

export function groupSampleOrdersIntoRoundTrips(
  orders: Record<string, unknown>[]
): GroupedSampleRoundTrip[] {
  const byPosition = new Map<string, GroupedSampleRoundTrip>();

  for (const order of orders) {
    const positionCode = String(
      order.positionCode ??
        order.code ??
        getSampleOrderLeg(order)?.positionCode ??
        order.id ??
        ""
    );
    if (!positionCode) continue;

    const trip =
      byPosition.get(positionCode) ??
      ({
        positionCode,
        reference: extractReferenceFromPositionCode(positionCode),
        instrument: String(order.instrument ?? order.symbol ?? "—"),
      } as GroupedSampleRoundTrip);

    const effect = getSampleOrderPositionEffect(order);
    if (effect === "OPEN") trip.open = order;
    else if (effect === "CLOSE") trip.close = order;

    byPosition.set(positionCode, trip);
  }

  return Array.from(byPosition.values()).filter((trip) => trip.open || trip.close);
}

export function shouldGroupSampleOrdersAsRoundTrips(
  importMode?: string,
  layoutShapeId?: string
): boolean {
  if (importMode === "closed_trade_paired" || importMode === "closed_trade_single") {
    return true;
  }
  if (
    layoutShapeId === "single_open_close_times" ||
    layoutShapeId?.startsWith("paired_")
  ) {
    return true;
  }
  return false;
}

function formatNumber(value: unknown): string {
  const num = Number(value);
  if (!Number.isFinite(num)) return "—";
  return num.toLocaleString(undefined, { maximumFractionDigits: 5 });
}

export function formatSampleOrderFillLine(
  order: Record<string, unknown>,
  label: string,
  timeZone: string
): string {
  const side = String(order.type ?? order.side ?? "—");
  const qty = order.quantity ?? getSampleOrderLeg(order)?.quantity;
  const price = order.price ?? getSampleOrderLeg(order)?.averagePrice;
  const timeRaw = String(order.transactionTime ?? order.createTime ?? "");
  const time = timeRaw ? formatTradeDateTimeDetailForDisplay(timeRaw, timeZone) : "—";
  const qtyPart = qty != null && qty !== "" ? ` · qty ${formatNumber(qty)}` : "";
  const pricePart = price != null && price !== "" ? ` @ ${formatNumber(price)}` : "";
  return `${label} · ${time} · ${side}${qtyPart}${pricePart}`;
}

export function formatSampleOrderPl(order: Record<string, unknown> | undefined): string | null {
  if (!order) return null;
  const pl = order.account_currency_pl ?? order.pl ?? order.profit;
  if (pl == null || pl === "") return null;
  const num = Number(pl);
  if (!Number.isFinite(num)) return null;
  const sign = num >= 0 ? "+" : "";
  return `${sign}${num.toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
}
